import { ArrowDownWideNarrow, BookOpen, ListOrdered, Loader2, RefreshCw, Target } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { BookAiPurpose, PurposePassage, PurposePlanAction } from '@/api/services/book-ai-purpose'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { useBookAiPurposeStore, useCurrentPurpose } from '@/store/useBookAiPurposeStore'

import { PurposeOrigin } from './purpose-bar'
import { PurposeEditor } from './purpose-editor'
import { PURPOSE_MARK_COLOR } from './skim-highlight'
import type { BookAiReaderState } from './useBookAi'

type Props = {
  bookAi: BookAiReaderState
  onNavigate: (key: number) => void
}

const LEVEL_STYLE: Record<'high' | 'mid' | 'low', { label: string; color: string }> = {
  high: { label: '関連 高', color: PURPOSE_MARK_COLOR },
  mid: { label: '関連 中', color: '#d8b4fe' },
  low: { label: '関連 低', color: 'transparent' },
}

const ACTION_STYLE: Record<PurposePlanAction, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  read: { label: '先に読む', variant: 'default' },
  skim: { label: '流し読み', variant: 'secondary' },
  skip: { label: '後回し', variant: 'outline' },
}

const FIT_NOTICE: Record<'partial' | 'weak', string> = {
  partial: 'この本は、目的と一部だけ関係しています。上の目的から「AI と磨く」で、この本に合う形に磨けます',
  weak: 'この本には、目的に直接関係する箇所が少ないようです。上の目的から「AI と磨く」で磨くか、別の本も検討してください',
}

const INITIAL_PASSAGES = 5

const INTRO =
  'この本を読む目的を入れると、目的に効く章と箇所・読み方の提案・目的に合わせた答えと問いを出します。目的はあなただけのもので、本の AI 解析はそのまま使います'

/**
 * 「本の全体」の読み方: 読む目的に合わせた読み方（利用者ごと）
 *
 * - 目的に効く章と読み方の提案: 章ごとの関連の強さ（目的と本文の埋め込みの近さ）と、AI の「先に読む／流し読み／後回し」
 * - 目的に効く箇所: 目的に近い本文の断片。押すとその箇所へ移動する
 * 目的の書き換え・削除はシートのヘッダーの目的から行う。
 */
export const PurposeTab = ({ bookAi, onNavigate }: Props) => {
  const isLoaded = useBookAiPurposeStore((state) => state.episodeId === bookAi.episodeId && state.isLoaded)
  const purpose = useCurrentPurpose(bookAi.episodeId)

  if (!isLoaded) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size={20} />
      </div>
    )
  }

  if (!purpose) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">{INTRO}</p>
        <PurposeEditor />
      </div>
    )
  }

  if (!purpose.map) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">AI 解析が終わると、目的に効く章と箇所を出せます</p>
    )
  }

  return (
    <div className="space-y-5">
      <PurposeOrigin episodeId={bookAi.episodeId} />
      {purpose.map.fit !== 'good' && (
        <p className="rounded-lg bg-amber-500/10 p-2.5 text-xs text-amber-800 dark:text-amber-200">
          {FIT_NOTICE[purpose.map.fit]}
        </p>
      )}
      <ReadingPlan bookAi={bookAi} purpose={purpose} onNavigate={onNavigate} />
      <PassageList passages={purpose.map.passages} onNavigate={onNavigate} />
    </div>
  )
}

/** 目的に効く章（関連の強さ）と読み方の提案を 1 つの一覧にまとめる */
const ReadingPlan = ({ bookAi, purpose, onNavigate }: Props & { purpose: BookAiPurpose }) => {
  const isPlanning = useBookAiPurposeStore((state) => state.isPlanning)
  const requestPlan = useBookAiPurposeStore((state) => state.requestPlan)
  const [order, setOrder] = useState<'relevance' | 'book'>('relevance')
  const sections = purpose.map?.sections
  const plan = purpose.plan
  const planBySection = useMemo(() => new Map(plan?.sections.map((s) => [s.index, s])), [plan])
  const rows = useMemo(
    () => (order === 'book' ? (sections ?? []) : [...(sections ?? [])].sort((a, b) => b.score - a.score)),
    [sections, order],
  )

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold text-muted-foreground">目的に効く章と読み方</h3>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2! text-xs"
          onClick={() => setOrder(order === 'book' ? 'relevance' : 'book')}
        >
          {order === 'book' ? <ListOrdered className="size-3.5" /> : <ArrowDownWideNarrow className="size-3.5" />}
          {order === 'book' ? '本の順' : '関連の高い順'}
        </Button>
      </div>

      {plan ? (
        <p className="rounded-lg bg-muted/60 p-2.5 text-sm leading-relaxed wrap-break-word">{plan.approach}</p>
      ) : (
        <Button variant="outline" size="sm" className="w-full" disabled={isPlanning} onClick={requestPlan}>
          {isPlanning ? <Loader2 className="size-4 animate-spin" /> : <Target className="size-4" />}
          {isPlanning ? '読み方を考えています…' : '章ごとの読み方を提案してもらう'}
        </Button>
      )}
      {plan && purpose.plan_outdated && (
        <Button variant="ghost" size="sm" className="w-full text-xs" disabled={isPlanning} onClick={requestPlan}>
          {isPlanning ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
          解析が新しくなったので提案を作り直す
        </Button>
      )}

      <ul className="space-y-1">
        {rows.map((section) => {
          const planned = planBySection.get(section.index)
          const level = LEVEL_STYLE[section.level]
          const isCurrent = section.index === bookAi.currentSectionIndex
          return (
            <li key={section.index}>
              <button
                type="button"
                onClick={() => onNavigate(section.start)}
                className={cn(
                  'flex w-full gap-2 rounded-lg px-2 py-2 text-left hover:bg-muted/60',
                  isCurrent && 'bg-muted/40',
                )}
              >
                <span
                  title={level.label}
                  className="w-1 shrink-0 self-stretch rounded-full border border-purple-300/40"
                  style={{ backgroundColor: level.color }}
                />
                <span className="min-w-0 flex-1 space-y-1">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="min-w-0 truncate text-sm font-medium">{section.title}</span>
                    {isCurrent && <span className="text-[11px] text-primary">今読んでいる章</span>}
                  </span>
                  <span className="flex flex-wrap items-center gap-1">
                    <span className="text-[11px] text-muted-foreground">{level.label}</span>
                    {planned && (
                      <Badge variant={ACTION_STYLE[planned.action].variant} className="h-5 px-1.5 text-[11px]">
                        {ACTION_STYLE[planned.action].label}
                      </Badge>
                    )}
                    {planned?.prerequisite && (
                      <Badge variant="outline" className="h-5 px-1.5 text-[11px]">
                        前提
                      </Badge>
                    )}
                    {section.passage_count > 0 && (
                      <span className="text-[11px] text-purple-600 dark:text-purple-300">
                        効く箇所 {section.passage_count}
                      </span>
                    )}
                  </span>
                  {planned?.reason && (
                    <span className="block text-xs text-muted-foreground wrap-break-word">{planned.reason}</span>
                  )}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/** 目的に近い本文の断片（近い順） */
export const PassageList = ({
  passages,
  onNavigate,
  title = '目的に効く箇所',
  initialCount = INITIAL_PASSAGES,
}: {
  passages: PurposePassage[]
  onNavigate: (key: number) => void
  title?: string
  /** 最初に出す数（残りは「もっと見る」で出す） */
  initialCount?: number
}) => {
  const [expanded, setExpanded] = useState(false)
  if (passages.length === 0) return null
  const visible = expanded ? passages : passages.slice(0, initialCount)
  return (
    <section className="space-y-2">
      <h3 className="flex items-center gap-1 text-xs font-semibold text-muted-foreground">
        <Target className="size-3.5 text-purple-500" />
        {title}
      </h3>
      <ul className="space-y-1.5">
        {visible.map((passage) => (
          <li key={passage.chunk_id}>
            <button
              type="button"
              onClick={() => onNavigate(passage.key)}
              className="w-full space-y-1 rounded-lg border-l-4 bg-muted/40 px-2.5 py-2 text-left hover:bg-muted/70"
              style={{ borderLeftColor: PURPOSE_MARK_COLOR }}
            >
              <span className="line-clamp-3 block text-sm wrap-break-word">{passage.excerpt}</span>
              <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <BookOpen className="size-3" />
                {passage.location.page != null ? `p.${passage.location.page + 1}・` : ''}
                {passage.section_title}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {passages.length > initialCount && (
        <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setExpanded(!expanded)}>
          {expanded ? '閉じる' : `もっと見る（${passages.length - initialCount}）`}
        </Button>
      )}
    </section>
  )
}
